import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { UncontrolledTooltip } from 'reactstrap';
import { injectIntl } from 'react-intl';

import Avatar from '../Avatar';
import AvatarPopover from '../AvatarPopover';
import DateTime from '../DateTime';

import { userIsAdmin } from '../../helpers/Auth';
import {
  getCollectionsList,
  getTermsList,
} from '../../helpers/FileHistory';
import { getSetting } from '../../helpers/Settings';

class FileHistoryEntry extends PureComponent {
  getUserData() {
    const { log } = this.props;
    const user = {
      uuid: null,
      name: 'anonymous',
      avatar_url: null,
    };

    if (log.user) {
      if (log.user.uuid) {
        user.uuid = log.user.uuid;
      }
      if (log.user.name) {
        user.name = log.user.name;
      }
      if (log.user.avatar_url) {
        user.avatar_url = log.user.avatar_url;
      }
    }

    return user;
  }

  getActionText() {
    const { intl, log } = this.props;

    let text = '';

    if (log.action === 'file_created' && !log.diff?.upload_page_url) {
      text = intl.formatMessage({ defaultMessage: 'uploaded file' });
    }
    if (log.action === 'file_created' && log.diff?.upload_page_url) {
      text = intl.formatMessage({ defaultMessage: 'uploaded file via Guest Upload:' });
    }
    if (log.action === 'file_imported') {
      text = intl.formatMessage({ defaultMessage: 'imported file' });
      if (log.source_name) {
        text = intl.formatMessage({ defaultMessage: '{text} from {source}' }, { text, source: log.source_name });
      }
    }
    if (log.action === 'file_updated') {
      text = intl.formatMessage({ defaultMessage: 'edited file' });
    }
    if (log.action === 'file_public') {
      if (log.diff === true) {
        text = intl.formatMessage({
          defaultMessage: 'changed file visibility to <span>Open</span>',
        }, {
          span: msg => <span className="file-history-access"><i className="fas fa-eye" /> {msg}</span>,
        });
      } else {
        text = intl.formatMessage({
          defaultMessage: 'changed file visibility to <span>Restricted</span>',
        }, {
          span: msg => <span className="file-history-access"><i className="fas fa-lock" /> {msg}</span>,
        });
      }
    }
    if (log.action === 'file_restricted') {
      if (log.diff === true) {
        text = intl.formatMessage({
          defaultMessage: 'changed file visibility to <span>Restricted</span>',
        }, {
          span: msg => <span className="file-history-access"><i className="fas fa-lock" /> {msg}</span>,
        });
      }
    }
    if (log.action === 'file_archive') {
      if (log.diff === true) {
        text = intl.formatMessage({ defaultMessage: 'archived file' });
      } else {
        text = intl.formatMessage({ defaultMessage: 'unarchived file' });
      }
    }
    if (log.action === 'file_status') {
      if (log.diff.new === null) {
        text = intl.formatMessage({ defaultMessage: 'removed file status' });
      } else {
        text = intl.formatMessage({
          defaultMessage: 'changed file status to <span>{name}</span>',
        }, {
          name: log.diff.new.name,
          span: msg => (
            <span className="file-history-access">
              <i className="file-status" style={{ backgroundColor: log.diff.new.color }} /> {msg}
            </span>
          ),
        });
      }
    }
    if (log.action === 'file_deleted') {
      text = intl.formatMessage({ defaultMessage: 'deleted file' });
    }
    if (log.action === 'file_restored') {
      text = intl.formatMessage({ defaultMessage: 'restored file' });
    }
    if (log.action === 'download') {
      text = intl.formatMessage({ defaultMessage: 'downloaded file' });
      if (log.share_link_url) {
        text = intl.formatMessage({
          defaultMessage: '{text} from <link>share link</link>',
        }, {
          text,
          link: msg => (
            <a href={log.share_link_url} target="_blank" rel="noreferrer">
              <strong>{msg}</strong>
            </a>
          ),
        });
      }
    }
    if (log.action === 'download_request') {
      text = intl.formatMessage({ defaultMessage: 'requested file' });
      if (userIsAdmin()) {
        text = intl.formatMessage({
          defaultMessage: '{text} <link>See download request</link>',
        }, {
          text,
          link: msg => {
            const url = `/download-requests/${log.diff.uuid}/`;
            return (
              <a href={url} target="_blank" title={msg} rel="noreferrer">
                <i className="fas fa-info-circle" />
              </a>
            );
          },
        });
      }
    }
    if (log.action === 'share') {
      text = intl.formatMessage({ defaultMessage: 'shared file' });
    }
    if (log.action === 'file_version_created') {
      text = intl.formatMessage({ defaultMessage: 'uploaded new file version' });
    }
    if (log.action === 'file_version_restored') {
      text = intl.formatMessage({ defaultMessage: 'restored previous file version' });
    }
    if (log.action === 'file_version_deleted') {
      text = intl.formatMessage({ defaultMessage: 'deleted file version' });
    }
    if (log.action === 'collections_attached') {
      text = intl.formatMessage({
        defaultMessage: 'added file to {count, plural, one {Collection} other {Collections}}: {collections}',
      }, {
        count: log.diff.length,
        collections: getCollectionsList(log),
      });
    }
    if (log.action === 'collections_detached') {
      text = intl.formatMessage({
        defaultMessage: 'removed file from {count, plural, one {Collection} other {Collections}}: {collections}',
      }, {
        count: log.diff.length,
        collections: getCollectionsList(log),
      });
    }
    if (log.action === 'terms_attached' || log.action === 'terms_detached') {
      let taxonomyName = log.diff.length === 1 ? intl.formatMessage({ defaultMessage: 'tag' }) :
        intl.formatMessage({ defaultMessage: 'tags' });
      if (log.object) {
        taxonomyName = log.diff.length === 1 ? log.object.name : log.object.plural;
      }
      if (log.action === 'terms_attached') {
        text = intl.formatMessage({
          defaultMessage: 'added {taxonomy}: {terms}',
        }, {
          taxonomy: taxonomyName,
          terms: getTermsList(log),
        });
      }
      if (log.action === 'terms_detached') {
        text = intl.formatMessage({
          defaultMessage: 'removed {taxonomy}: {terms}',
        }, {
          taxonomy: taxonomyName,
          terms: getTermsList(log),
        });
      }
    }
    if (log.action === 'allowed_groups_attached') {
      const items = userIsAdmin() ? getTermsList(log, '/admin/teams/') : getTermsList(log);
      text = intl.formatMessage({
        defaultMessage: 'added {count, plural, one {team} other {teams}} to collaborate: {items}',
      }, {
        count: log.diff.length,
        items,
      });
    }
    if (log.action === 'allowed_groups_detached') {
      const items = userIsAdmin() ? getTermsList(log, '/admin/teams/') : getTermsList(log);
      text = intl.formatMessage({
        defaultMessage: 'removed {count, plural, one {team} other {teams}} from collaborating: {items}',
      }, {
        count: log.diff.length,
        items,
      });
    }
    if (log.action === 'allowed_users_attached') {
      const items = userIsAdmin() ? getTermsList(log, '/admin/users/') : getTermsList(log);
      text = intl.formatMessage({
        defaultMessage: 'added {count, plural, one {user} other {users}} to collaborate: {items}',
      }, {
        count: log.diff.length,
        items,
      });
    }
    if (log.action === 'allowed_users_detached') {
      const items = userIsAdmin() ? getTermsList(log, '/admin/users/') : getTermsList(log);
      text = intl.formatMessage({
        defaultMessage: 'removed {count, plural, one {user} other {users}} from collaborating: {items}',
      }, {
        count: log.diff.length,
        items,
      });
    }
    if (log.action === 'liked') {
      text = intl.formatMessage({ defaultMessage: 'liked file' });
    }
    if (log.action === 'unliked') {
      text = intl.formatMessage({ defaultMessage: 'unliked file' });
    }
    if (log.action === 'collection_featured_image') {
      text = intl.formatMessage({
        defaultMessage: 'set file as Collection thumbnail: {collections}',
      }, {
        collections: getCollectionsList(log),
      });
    }
    if (log.action === 'merged') {
      text = intl.formatMessage({
        defaultMessage: 'merged {count} {count, plural, one {file} other {files}}',
      }, {
        count: log.diff,
      });
    }

    return text;
  }

  getUploadPageUploadedByText() {
    const { intl, log } = this.props;

    let text = '';

    if (log.diff?.upload_page_access_name) {
      text += `${log.diff?.upload_page_access_name}`;
    }

    if (text.length > 0 && log.diff?.upload_page_access_email) {
      text += ' ';
    }

    if (log.diff?.upload_page_access_email) {
      text += `(${log.diff?.upload_page_access_email})`;
    }

    if (text.length === 0) {
      text += intl.formatMessage({ defaultMessage: 'Someone' });
    }

    return text;
  }

  render() {
    const {
      intl,
      log,
      handleOpenDiffModal,
    } = this.props;
    const user = this.getUserData();

    return (
      <div className="user-item file-history-item">
        <Avatar
          id={`user-log-${log.id}`}
          user={user}
          size={30}
          expandable={!!user.uuid}
        />
        <div className="user-item-body">
          <h6 className="user-item-title file-history-text">
            <span className="user-name">
              {(log.action === 'file_created' && log.diff?.upload_page_uuid) ? (
                <>
                  {this.getUploadPageUploadedByText()}
                </>
              ) : (
                <>
                  {user.uuid ? (
                    <>
                      <a id={`user-name-log-${log.id}`} href="#profile" onClick={(e) => { e.preventDefault(); }}>
                        {user.name}
                      </a>
                      <AvatarPopover
                        user={user}
                        targetId={`user-name-log-${log.id}`}
                      />
                    </>
                  ) : (
                    <span>{user.name}</span>
                  )}
                </>
              )}
            </span>
            {this.getActionText()}
            {(log.action === 'file_created' && log.diff?.upload_page_title) && (
              <>
                {' '}
                <a href={`${getSetting('workspace_url')}/u/${log.diff?.upload_page_url}`}>
                  {log.diff?.upload_page_title}
                </a>
              </>
            )}
            {(log.action === 'file_updated' || log.action === 'file_created') && (
              <>
                {' '}
                <a
                  href="#details"
                  title="details"
                  onClick={(e) => {
                    e.preventDefault();
                    handleOpenDiffModal(log);
                  }}
                >
                  <i className="fas fa-info-circle" />
                </a>
              </>
            )}
            {(user.name === 'anonymous' || !user.uuid) && (
              <>
                {' '}
                <span className="file-history-anonymous" id={`log-anonymous-${log.id}`}>
                  <i className="fas fa-info-circle" />
                </span>
                <UncontrolledTooltip placement="bottom" target={`log-anonymous-${log.id}`}>
                  {log.action === 'download' && (
                    intl.formatMessage({ defaultMessage: 'This file was downloaded from a share link' })
                  )}
                  {(log.action === 'liked' || log.action === 'unliked') && (
                    intl.formatMessage({ defaultMessage: 'This action happened on a share link' })
                  )}
                </UncontrolledTooltip>
              </>
            )}
            <DateTime
              id={`log-date-${log.id}`}
              className="file-history-date"
              dateTime={log.created_at}
            />
            {log.file && (
              <span className="file-history-link">
                <Link
                  to={{
                    pathname: `/files/${log.file.uuid}`,
                    state: {
                      modal: true,
                      fromDashboard: true,
                    },
                  }}
                >
                  <i className="far fa-file" />
                </Link>
              </span>
            )}
          </h6>
        </div>
      </div>
    );
  }
}

FileHistoryEntry.propTypes = {
  intl: PropTypes.object.isRequired,
  log: PropTypes.object.isRequired,
  handleOpenDiffModal: PropTypes.func.isRequired,
};

export default injectIntl(FileHistoryEntry);
